import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorLuftskrueSidderFast() {
  return (
    <ArticleLayout
      title="Luftskruen sidder fast eller er rundet: Hvad du må og ikke må"
      metaTitle="Fastgroet luftskrue på radiator? Løsninger og råd"
      metaDescription="Sidder luftskruen urokkeligt fast eller er tappen rundet? Lær hvad du må prøve med rustløsner, og hvornår der skal tilkaldes en fagmand."
      canonicalPath="/radiator-luftskrue-sidder-fast"
      introLead="En fastgroet luftskrue skyldes som regel tørrede kalkaflejringer eller overfladerust i det fine messinggevind efter mange års inaktivitet."
      introAnswer="Hvis den lille firkantede tap ikke vil give sig ved almindelig håndkraft med radiatornøglen, må du aldrig bruge rå vold eller store rørtænger. En knækket luftskrue medfører ukontrollabelt vandudslip under driftstryk. Løsningen er forsigtig brug af krybeolie eller udskiftning af hele udlufterproppen."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Sådan løsner du skruen skånsomt' },
        { id: 'hvad-maa-man', title: '2. Det må du gøre selv' },
        { id: 'forbudt', title: '3. Det må du ALDRIG gøre' },
        { id: 'rundet-tap', title: '4. Hvad hvis firkanten er rundet?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 sikre trin til en genstridig luftskrue"
      checklist={[
        {
          title: 'Brug altid en præcis 5 mm messing- eller stålnøgle',
          description: 'En slidt eller forkert dimensioneret nøgle glider af og deformerer metallet lynhurtigt.',
        },
        {
          title: 'Påfør en dråbe krybeolie direkte på gevindet',
          description: 'Giv olien (fx WD-40 Specialist rustløsner) 15–30 minutter til at trække ind i de mikroskopiske gevindgange.',
        },
        {
          title: 'Giv luftskruen et par helt lette slag med skruetrækkerskaftet',
          description: 'En let vibration hjælper med at bryde kalkkrystallerne i gevindet uden at bøje tappen.',
        },
        {
          title: 'Vrik nøglen forsigtigt frem og tilbage i bittesmå ryk',
          description: 'Prøv først at spænde en mikrometer med uret, før du drejer mod uret for at løsne.',
        },
        {
          title: 'Stop omgående hvis tappen begynder at give efter eller vrides skæv',
          description: 'Mærker du metaltræthed i tappen, skal arbejdet indstilles straks før skruen knækker.',
        },
      ]}
      sections={[
        {
          id: 'hvad-maa-man',
          title: 'Hvad må du gøre selv for at løsne skruen?',
          content: (
            <>
              <p>
                En radiatorluftskrue er fremstillet af blødt messing eller forniklet stål for ikke at ruste ubehjælpeligt fast i radiatorens jernprop.
              </p>
              <p>
                Du må anvende godkendte penetrerende krybeolier og lette mekaniske bank på skruens center. Hvis gevindet slipper, skrues den en halv omgang ud for at lufte radiatoren, hvorefter den spændes til igen. Husk at tørre overskydende olie grundigt af, så det ikke tiltrækker snavs eller lugter ved opvarmning.
              </p>
            </>
          ),
        },
        {
          id: 'forbudt',
          title: 'Det må du ALDRIG gøre ved en fastgroet luftskrue',
          content: (
            <>
              <p>
                Hver vinter oplever forsikringsselskaber og VVS-vagter alvorlige vandskader, fordi boligejere overvurderer luftskruens holdbarhed:
              </p>
              <p>
                <strong>Ingen store tænger eller forlængerrør:</strong> En stor vandpumpetang eller svensknøgle kan let overføre et moment på 30–50 Nm. En 5 mm luftskrue knækker ved under 5 Nm.
              </p>
              <p>
                <strong>Ingen gasbrænder eller varmepistol:</strong> Opvarmning af ventilen med åben ild kan koge vandet i kammeret, ødelægge o-ringe og overophedningssikringer eller antænde vægbeklædning.
              </p>
              <p>
                <strong>Ingen boremaskine eller skrueudtrækker:</strong> Forsøg aldrig at bore en knækket tap ud, mens der er vand og tryk på anlægget. Går boret igennem, skyller 70 °C varmt vand ud med fuldt tryk.
              </p>
            </>
          ),
        },
        {
          id: 'rundet-tap',
          title: 'Hvad gør man, hvis den firkantede tap er rundet helt af?',
          content: (
            <>
              <p>
                Hvis en tidligere beboer har ødelagt firkanten med en bidetang, kan en almindelig radiatornøgle ikke længere gribe fat.
              </p>
              <p>
                Løsningen i dette tilfælde er ikke at kæmpe med selve skruen, men at udskifte hele den 1/2" udlufterprop, som skruen sidder i. En ny komplet luftskrueprop koster typisk under 50 kr. i materialer, men monteringen kræver, at anlægget enten tappes af eller fryses ned ved rørene.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-montøren tilkaldes?',
        content: (
          <p>
            Hvis luftskruen er knækket plan med godset, hvis der drypper vand konstant fra en skrue der ikke kan lukkes, eller hvis hele radiatoren skal have ny udlufterprop, skal en autoriseret VVS-installatør tilkaldes. De råder over rørefryseudstyr, så radiatoren kan serviceres uden at hele ejendommen skal tappes for vand.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Kan man udlufte en radiator uden luftskrue?',
          a: 'Nej. Forsøg aldrig at løsne unioner eller forskruninger på tilgangsrøret for at lufte ud. Uden en kontrollerbar ventil risikerer du, at pakningen skrider, og vandet fosser ud.',
        },
        {
          q: 'Hvad koster det at få skiftet en luftskrue hos en VVS’er?',
          a: 'Selve reservedelen koster 30–75 kr. Den samlede regning afhænger af arbejdsløn og kørsel (typisk 800–1.600 kr.), hvis anlægget skal tappes eller fryses ned.',
        },
        {
          q: 'Findes der automatiske luftskruer?',
          a: 'Ja, der findes automatiske radiatorudluftere med svømmer eller fibermembran, som automatisk lukker luft ud, men forsegler når vandet rammer. Disse kan monteres i stedet for den manuelle prop.',
        },
      ]}
      internalLinks={[
        { to: '/udluftning-af-radiator', text: 'Udluftning af radiator', desc: 'normal udluftningsprocedure' },
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'overblik over andre årsager' },
        { to: '/radiator-laekker', text: 'Radiatoren lækker', desc: 'håndtering af utætheder og vanddryp' },
        { to: '/radiator-udskiftning-pris', text: 'Pris på radiatorudskiftning', desc: 'oversigt over VVS-timepriser og reservedele' },
        { to: '/radiatornoegle', text: 'Radiatornøgle: Hvilken nøgle passer?', desc: 'luftskruetyper og det rigtige værktøj' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
      ]}
    />
  );
}
